import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AssetSymbol, BlockType } from '@tradingblock/types';
import { BlockDragHandle, HttpAutocomplete, OptionType, getBlockTypeName, Pill } from '@tradingblock/components';
import { SymbolToOption, useSymbolSearch } from '../../hooks/useSymbolSearch';
import { FavoriteButton } from '../../components/blocks/FavoriteButton';
import NewsSubscriptionModal from './NewsSubscriptionModal';
import { InfoDataActions } from '../../data/global/actions/InfoActions';
import { useBlockId } from '../../components/blocks/BlockState';
import { SymbolChannels } from '../../types';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../data/global/dataSelectors';
import { useGroupAssetSymbol } from '../useGroupState';
import { useInfoSettings } from './hooks/useInfoSettings';
import _ from 'lodash';
import { NewsStatusIcon } from '../../components/dashboard/NewsStatusIcon';
import { SymbolItem } from './SymbolItem';
import { ChannelEditModal } from './ChannelEditModal';

interface InfoSymbolPickerHeaderProps {
  blockType: BlockType;
  symbol: AssetSymbol | undefined;
  isLoaded: boolean | undefined;
  onSelect: (value: OptionType<AssetSymbol>) => void;
  onClear?: () => void;
  placeholder?: string;
  clearOptionsOnSelect?: boolean;
  focus?: boolean;
  disableFavorite?: boolean;
  hidePrice?: boolean;
  hideTime?: boolean;
  hidePercent?: boolean;
  onSearch?: (value: string, limit?: number | undefined) => void;
  onBlur?: () => void;
}

export const InfoSymbolPickerHeader: React.FunctionComponent<InfoSymbolPickerHeaderProps> = ({
  blockType,
  symbol,
  isLoaded,
  onSelect,
  placeholder,
  clearOptionsOnSelect,
  focus,
  onClear,
  disableFavorite,
  onSearch,
  onBlur,
}) => {
  const dispatch = useDispatch();
  const blockId = useBlockId();
  const newsBlock = useStateSelector(s => s.information.marketNews[blockId]);
  const newsFeedStatus = useStateSelector(s => s.newsFeedProvider.status);
  const symbolRequest = useMemo(() => {
    return newsBlock && newsBlock.symbolRequest;
  }, [newsBlock && newsBlock.symbolRequest]);
  const currentFilters = useMemo(() => {
    return newsBlock && newsBlock.filteredNewsSymbols;
  }, [newsBlock && newsBlock.filteredNewsSymbols]);

  const isShowingAllSymbols = useMemo(
    () => !currentFilters || currentFilters.length === 0,
    [currentFilters]
  );

  const search = useSymbolSearch();
  const symbolValue = symbol ? symbol.symbol : '';
  const autoCompleteValue = useMemo(() => {
    return symbol ? SymbolToOption(symbol, true) : undefined;
  }, [symbol]);
  const groupAsset = useGroupAssetSymbol();
  const rest = useMemo(() => {
    return { autoFocus: focus };
  }, [focus]);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [hoveredSymbolIndex, setHoveredSymbolIndex] = useState<number | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [editingSymbol, setEditingSymbol] = useState<SymbolChannels | null>(null);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const settings = useInfoSettings();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [symbolListHover, setSymbolListHover] = useState(false);

  const channelMappings = [
    { active: settings.dailyNewsfeed, name: 'DailyNewsfeed' },
    { active: settings.corporateActionsDividends, name: 'CorporateActionsDividends' },
    { active: settings.earningsReports, name: 'EarningsReports' },
    { active: settings.rumorsAndIdeas, name: 'RumorsAndIdeas' },
    { active: settings.companyNews, name: 'CompanyNews' },
    { active: settings.sectorNews, name: 'SectorNews' },
    { active: settings.ipos, name: 'Ipos' },
    { active: settings.economicsAndGovernment, name: 'EconomicsAndGovernment' },
    { active: settings.theMarkets, name: 'TheMarkets' },
    { active: settings.etfCoverage, name: 'EtfCoverage' },
    { active: settings.options, name: 'Options' },
  ];

  const channelArray: string[] = [];
  // Populate channelArray for MarketNews polling request based on useInfoSettings()
  if (_.isEmpty(settings)) {
    channelArray.push('DailyNewsfeed');
  } else {
    channelMappings.forEach(channelObj => {
      if (channelObj.active) channelArray.push(channelObj.name);
    });
  }

  // All available channels (for ChannelEditModal)
  const allAvailableChannels = [
    'DailyNewsfeed',
    'CorporateActionsDividends',
    'EarningsReports',
    'RumorsAndIdeas',
    'CompanyNews',
    'SectorNews',
    'Ipos',
    'EconomicsAndGovernment',
    'TheMarkets',
    'EtfCoverage',
    'Options',
  ];

  const isDJIA = (groupAsset && groupAsset.symbol === '$DJIA') || (groupAsset && groupAsset.symbol === '$DJI');
  const isSPX = groupAsset && groupAsset.symbol === '$SPX';
  const isNasdaq = groupAsset && groupAsset.symbol.includes('$NDX');
  const isVIX = groupAsset && groupAsset.symbol === '$VIX';
  const isUnsupportedIndex = groupAsset && !isDJIA && !isSPX && !isNasdaq && !isVIX && groupAsset.symbol[0] === '$';

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (symbol) {
      handleUpdateFilterNews([symbol.symbol]);
      handleAddNews(symbol.symbol);
    }
  }, [symbol]);

  const handleAddNews = useCallback(
    (symbol: string) => {
      if (!isUnsupportedIndex && symbolRequest && !symbolRequest.find(s => s.name === symbol)) {
        const addSymbol: SymbolChannels = {
          name: symbol,
          channels: channelArray,
        };

        dispatch(InfoDataActions.editMarketNewsBlock({ blockId: blockId, symbols: [...symbolRequest, addSymbol] }));
      }
    },
    [symbolRequest]
  );

  function toggleFilterNews(symbol: string) {
    if (currentFilters.includes(symbol)) {
      handleUpdateFilterNews(currentFilters.filter(f => f !== symbol));
    } else {
      const updated = [...currentFilters, symbol];
      const allSelected = symbolRequest && updated.length >= symbolRequest.length;
      handleUpdateFilterNews(allSelected ? [] : updated);
    }
  }

  function handleUpdateFilterNews(filter: string[] = []) {
    dispatch(InfoDataActions.filterMarketNewsBlock({ blockId: blockId, symbols: [...filter] }));
  }

  useEffect(() => {
    const filterMatchesList =
      currentFilters &&
      currentFilters.length > 0 &&
      symbolRequest &&
      symbolRequest.length > 0 &&
      currentFilters.length === symbolRequest.length &&
      symbolRequest.every(sc => currentFilters.includes(sc.name));
    if (filterMatchesList) {
      dispatch(InfoDataActions.filterMarketNewsBlock({ blockId, symbols: [] }));
    }
  }, [currentFilters, symbolRequest, blockId, dispatch]);

  const handleRemoveNews = useCallback(
    (symbol: string) => {
      const remainingSymbols: SymbolChannels[] = symbolRequest.filter(s => s.name !== symbol);
      dispatch(InfoDataActions.editMarketNewsBlock({ blockId: blockId, symbols: [...remainingSymbols] }));
      handleUpdateFilterNews(currentFilters.filter(f => f !== symbol));
    },
    [symbolRequest, currentFilters]
  );

  const handleEditChannels = useCallback((symbol: SymbolChannels, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingSymbol(symbol);
    setSelectedChannels(symbol.channels);
  }, []);

  const handleCloseChannelModal = useCallback(() => {
    setEditingSymbol(null);
    setSelectedChannels([]);
  }, []);

  const handleToggleChannel = useCallback((channel: string) => {
    setSelectedChannels(prev =>
      prev.includes(channel)
        ? prev.filter(c => c !== channel)
        : [...prev, channel]
    );
  }, []);

  const handleSaveChannels = useCallback(() => {
    if (editingSymbol && selectedChannels.length > 0) {
      const updatedSymbol: SymbolChannels = {
        ...editingSymbol,
        channels: selectedChannels,
      };
      const updatedSymbols = symbolRequest.map(s => s.name === updatedSymbol.name ? updatedSymbol : s);
      dispatch(InfoDataActions.editMarketNewsBlock({ blockId: blockId, symbols: updatedSymbols }));
      handleCloseChannelModal();
    }
  }, [editingSymbol, selectedChannels, symbolRequest, blockId]);

  const addSpaceBeforeCaps = useCallback((str: string) => {
    return str.replace(/(?<=[A-Z][a-z]*)(?=[A-Z])/g, ' ');
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (!currentFilters || currentFilters.length === 0) {
      container.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }

    const timeoutId = setTimeout(() => {
      const lastFilteredSymbol = currentFilters[currentFilters.length - 1];
      const symbolEl = container.querySelector<HTMLElement>(`[data-symbol-name="${lastFilteredSymbol}"]`);
      if (!symbolEl) return;

      const containerRect = container.getBoundingClientRect();
      const symbolRect = symbolEl.getBoundingClientRect();

      if (symbolRect.left < containerRect.left) {
        scrollLeft();
      } else if (symbolRect.right > containerRect.right) {
        scrollRight();
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [currentFilters, symbolRequest, scrollLeft, scrollRight]);

  const SCROLL_END_TOLERANCE_PX = 4;

  const updateScrollArrows = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
    const hasOverflow = scrollWidth > clientWidth;
    setShowLeftArrow(hasOverflow && scrollLeft > SCROLL_END_TOLERANCE_PX);
    setShowRightArrow(hasOverflow && scrollLeft < maxScrollLeft - SCROLL_END_TOLERANCE_PX);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener('scroll', updateScrollArrows);
    const ro = new ResizeObserver(updateScrollArrows);
    ro.observe(container);
    const t = setTimeout(updateScrollArrows, 0);
    return () => {
      clearTimeout(t);
      container.removeEventListener('scroll', updateScrollArrows);
      ro.disconnect();
    };
  }, [symbolRequest, updateScrollArrows]);

  const scrollLeft = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollLeft, clientWidth } = container;
      const scrollAmount = Math.min(clientWidth, scrollLeft);
      if (scrollAmount > 0) {
        container.scrollTo({ left: scrollLeft - scrollAmount, behavior: 'smooth' });
        setTimeout(updateScrollArrows, 350);
      }
    }
  }, [updateScrollArrows]);

  const scrollRight = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const maxScrollLeft = scrollWidth - clientWidth;
      const remaining = maxScrollLeft - scrollLeft;
      if (remaining <= 0) return;
      const scrollAmount = Math.min(clientWidth, remaining);
      if (scrollAmount > 0) {
        container.scrollTo({ left: scrollLeft + scrollAmount, behavior: 'smooth' });
        setTimeout(updateScrollArrows, 350);
      }
    }
  }, [updateScrollArrows]);

  return (
    <div className="block-nav nav">
      <BlockDragHandle className="nav-item">{getBlockTypeName(blockType)}</BlockDragHandle>
      {(!newsFeedStatus || newsFeedStatus !== 'open') && (
        <div className="nav-item">
          <NewsStatusIcon />
        </div>
      )}
      {newsFeedStatus && newsFeedStatus === 'open' && (
        <>
          <div className="nav-item">
            <Pill>
              {isLoaded && (
                <HttpAutocomplete
                  onClear={onClear}
                  rest={rest}
                  closeMenuOnSelect={false}
                  blurInputOnSelect={false}
                  openMenuOnFocus={true}
                  controlShouldRenderValue={false}
                  backspaceRemovesValue={false}
                  commitSymbolToSearchOnBlur={true}
                  clearOnlyWhenInputMatchesSymbol={true}
                  minimumLength={1}
                  search={(value: string, limit?: number | undefined) => {
                    if (onSearch !== undefined) {
                      onSearch(value, limit);
                    }

                    return search(value, limit);
                  }}
                  onBlur={onBlur}
                  value={autoCompleteValue}
                  onSelect={onSelect}
                  clearOptionsOnSelect={clearOptionsOnSelect}
                  placeholder={placeholder}
                  symbol={symbolValue}
                  charLimit={20}
                />
              )}
            </Pill>
          </div>
          {symbol && (
            <>
              <div className="nav-item nav-stock-name">
                <div className="txt-sm">
                  <strong>{symbol.symbol}</strong>
                </div>
                <div className="txt-sm mute" title={symbol.name}>
                  {symbol.name}
                </div>
              </div>
            </>
          )}
          {symbolRequest && symbolRequest.length > 0 && (
            <div
              className="nav-item-last symbol-list-nav"
              style={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1, maxWidth: '100%', overflow: 'hidden' }}
              onMouseEnter={() => setSymbolListHover(true)}
              onMouseLeave={() => setSymbolListHover(false)}
            >
              <div
                className={`symbol-list-and-arrows${isShowingAllSymbols ? ' symbol-list-and-arrows--light-bg' : ''}`}
                style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', overflow: 'hidden' }}
              >
                {showLeftArrow && (
                  <span className="symbol-list-scroll-hint symbol-list-scroll-hint--left" aria-hidden />
                )}
                {showRightArrow && (
                  <span className="symbol-list-scroll-hint symbol-list-scroll-hint--right" aria-hidden />
                )}
                <div
                  ref={scrollContainerRef}
                  style={{
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    maxWidth: '100%',
                    flex: '1',
                    minWidth: 0,
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    ...(isShowingAllSymbols && { paddingRight: 4 }),
                  }}
                  className="symbol-list-scroll"
                >
                  <div
                    className="txt-sm"
                    style={{
                      whiteSpace: 'nowrap',
                      ...(isShowingAllSymbols && {
                        display: 'inline-block',
                        border: '1px solid var(--lighten3x, #dee2e6)',
                        borderRadius: 'var(--radius, 4px)',
                        padding: '2px 4px',
                        backgroundColor: 'var(--lighten, rgba(0,0,0,0.04))',
                      }),
                    }}
                  >
                    <strong>
                      {
                        symbolRequest.map((sc, i) => {
                          const hasActiveFilter = currentFilters && currentFilters.length > 0;
                          const isFiltered = hasActiveFilter && currentFilters.includes(sc.name);
                          const isHovered = hoveredSymbolIndex === i;
                          return (
                            <SymbolItem
                              key={sc.name}
                              symbol={sc}
                              index={i}
                              total={symbolRequest.length}
                              isFiltered={isFiltered}
                              hasActiveFilter={hasActiveFilter}
                              isHovered={isHovered}
                              symbolRef={() => { }}
                              onMouseEnter={() => {
                                if (hoverTimeoutRef.current) {
                                  clearTimeout(hoverTimeoutRef.current);
                                  hoverTimeoutRef.current = null;
                                }
                                setHoveredSymbolIndex(i);
                              }}
                              onMouseLeave={() => {
                                hoverTimeoutRef.current = setTimeout(() => {
                                  setHoveredSymbolIndex(null);
                                }, 300);
                              }}
                              onToggleFilter={(symbolName, e) => {
                                if (symbolRequest.length > 1) {
                                  toggleFilterNews(symbolName);
                                }
                              }}
                              onEditChannels={handleEditChannels}
                              onDeleteSymbol={(symbolName, e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemoveNews(symbolName);
                              }}
                              onPopupMouseEnter={() => {
                                if (hoverTimeoutRef.current) {
                                  clearTimeout(hoverTimeoutRef.current);
                                  hoverTimeoutRef.current = null;
                                }
                                setHoveredSymbolIndex(i);
                              }}
                            />
                          );
                        })
                      }
                    </strong>
                  </div>
                </div>
                {symbolListHover && showLeftArrow && (
                  <button
                    type="button"
                    className="symbol-list-scroll-arrow symbol-list-scroll-arrow--left"
                    onClick={scrollLeft}
                    title="Scroll Left"
                  >
                    <i className="fas fa-chevron-left" />
                  </button>
                )}
                {symbolListHover && showRightArrow && (
                  <button
                    type="button"
                    className="symbol-list-scroll-arrow symbol-list-scroll-arrow--right"
                    onClick={scrollRight}
                    title="Scroll Right"
                  >
                    <i className="fas fa-chevron-right" />
                  </button>
                )}
              </div>
              {!isShowingAllSymbols && (
                <button
                  type="button"
                  className="symbol-list-clear-filter"
                  title="Show All Symbols"
                  onClick={() => handleUpdateFilterNews([])}
                >
                  <i className="fas fa-filter txt-xs" />
                </button>
              )}
            </div>
          )}
        </>
      )}

      {showModal && <NewsSubscriptionModal show={showModal} setShow={setShowModal} removeNews={handleRemoveNews} />}
      {editingSymbol && (
        <ChannelEditModal
          editingSymbol={editingSymbol}
          show={!!editingSymbol}
          selectedChannels={selectedChannels}
          availableChannels={allAvailableChannels}
          onClose={handleCloseChannelModal}
          onSave={handleSaveChannels}
          onToggleChannel={handleToggleChannel}
          addSpaceBeforeCaps={addSpaceBeforeCaps}
        />
      )}
    </div>
  );
};
